import { loadUrlAsBlob } from "libs/files";

import { GeneratedSkedApiClient } from "../generated-client";
import type { components, paths } from "../generated/sked-api-openapi-types";
import { FileToUpload, UploadingStatus } from "./types";
import { transcodeVideoByUrl } from "./transcode-video-by-url";
import { uploadFileToS3 } from "./upload-file-to-s3";
import { CollectionFile } from "shared/types/files";
import {
  logUploadBreadcrumb,
  logUploadFailure,
  withUploadSpan,
} from "./upload-observability";
import { resolveMediaCorrelationKey } from "utils/media-observability";

type CreateAssetFromS3Operation =
  paths["/content-inbox/inbox/{inboxId}/upload/from-s3"]["post"];
type CreateAssetFromS3Body = NonNullable<
  CreateAssetFromS3Operation["requestBody"]
>["content"]["application/json"];
type CreateAssetFromTranscodingJobOperation =
  paths["/content-inbox/inbox/{inboxId}/upload/from-transcoding-job"]["post"];
type CreateAssetFromTranscodingJobBody = NonNullable<
  CreateAssetFromTranscodingJobOperation["requestBody"]
>["content"]["application/json"];
type GeneratedAsset = components["schemas"]["Asset"];

const generatedSkedApiClient = new GeneratedSkedApiClient<paths>();

function toCollectionFile(asset: GeneratedAsset): CollectionFile {
  const id = asset._id ?? asset.asset_id ?? "";

  return {
    _id: id,
    id,
    source: {
      name: asset.source?.name ?? "",
      notes: asset.source?.notes,
    },
    name: asset.name ?? "",
    description: asset.description ?? "",
    tags: asset.tags ?? [],
    active: asset.active ?? true,
    storage: {
      bucket: asset.storage?.bucket ?? "",
      key: asset.storage?.key ?? "",
      url: asset.storage?.url ?? "",
    },
    thumbnail: asset.thumbnail ?? "",
    inbox_id: asset.inbox_id ?? "",
    created: asset.created ?? "",
    updated: asset.updated ?? "",
    meta: {
      mime: "",
      format: "",
      size: 0,
      width: 0,
      height: 0,
      space: "",
      channels: 0,
      depth: "",
      density: 0,
      isProgressive: false,
      hasProfile: false,
      hasAlpha: false,
      isVideo: false,
      isImage: false,
      google: { labels: [] },
      chromaSubsampling: "",
      exif: {},
      ...asset.meta,
    },
    credential: {
      admin: asset.credential?.admin ?? false,
      auth_key: asset.credential?.auth_key ?? "",
      ext_auth: Boolean(asset.credential?.ext_auth),
    },
    original: asset.original ?? false,
    asset_id: asset.asset_id ?? id,
    transcodedVideo:
      asset.transcodedVideo?.bucket &&
      asset.transcodedVideo.key &&
      asset.transcodedVideo.url
        ? {
            bucket: asset.transcodedVideo.bucket,
            key: asset.transcodedVideo.key,
            url: asset.transcodedVideo.url,
          }
        : undefined,
    coverFrames: asset.coverFrames
      ?.filter(
        (coverFrame): coverFrame is { timestampMs: number; url: string } =>
          typeof coverFrame.timestampMs === "number" &&
          typeof coverFrame.url === "string"
      )
      .map((coverFrame) => ({
        timestampMs: coverFrame.timestampMs,
        url: coverFrame.url,
      })),
  };
}

function createUploadAttemptId() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  return `upload-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export async function uploadFileToCollection({
  collectionId,
  file,
}: {
  file:
    | {
        url: string;
        name: string;
        uploadAttemptId?: string;
        mediaCorrelationKey?: string;
      }
    | {
        transcodingJobId: string;
        sourceUrl?: string;
        name: string;
        uploadAttemptId?: string;
        mediaCorrelationKey?: string;
      };
  collectionId: string;
}) {
  return await ("transcodingJobId" in file
    ? uploadFromTranscodingJob({
        collectionId,
        file,
        observability: {
          uploadAttemptId: file.uploadAttemptId,
          mediaCorrelationKey: file.mediaCorrelationKey,
        },
      })
    : uploadFromS3({
        collectionId,
        file,
        observability: {
          uploadAttemptId: file.uploadAttemptId,
          mediaCorrelationKey: file.mediaCorrelationKey,
        },
      }));
}

async function uploadFromTranscodingJob({
  collectionId,
  file,
  observability,
}: {
  collectionId: string;
  file: { transcodingJobId: string; sourceUrl?: string; name: string };
  observability?: { uploadAttemptId?: string; mediaCorrelationKey?: string };
}) {
  const result = await generatedSkedApiClient.client.POST(
    "/content-inbox/inbox/{inboxId}/upload/from-transcoding-job",
    {
      params: {
        path: { inboxId: collectionId },
      },
      body: {
        transcoding: {
          jobId: String(file.transcodingJobId),
          originalFilename: file.name,
          sourceUrl: file.sourceUrl,
        },
        observability,
      } satisfies CreateAssetFromTranscodingJobBody,
    }
  );

  return toCollectionFile(await generatedSkedApiClient.unwrap(result));
}

async function uploadFromS3({
  collectionId,
  file,
  observability,
}: {
  file: { url: string; name: string };
  collectionId: string;
  observability?: { uploadAttemptId?: string; mediaCorrelationKey?: string };
}) {
  const result = await generatedSkedApiClient.client.POST(
    "/content-inbox/inbox/{inboxId}/upload/from-s3",
    {
      params: {
        path: { inboxId: collectionId },
      },
      body: {
        s3: {
          url: file.url,
          originalFilename: file.name,
        },
        observability,
      } satisfies CreateAssetFromS3Body,
    }
  );

  return toCollectionFile(await generatedSkedApiClient.unwrap(result));
}

interface FileUploadParams {
  onProgress(options: { value: number; status: UploadingStatus }): void;
}

export async function uploadFile(
  file: FileToUpload,
  { onProgress }: FileUploadParams
): Promise<
  | { url: string; uploadAttemptId: string; mediaCorrelationKey: string }
  | {
      transcodingJobId: string;
      sourceUrl: string;
      uploadAttemptId: string;
      mediaCorrelationKey: string;
    }
> {
  const uploadAttemptId = createUploadAttemptId();
  const metadata = {
    uploadAttemptId,
    fileName: file.name,
    fileType: file.type,
    contentType: file.contentType,
    mediaCorrelationKey: resolveMediaCorrelationKey({
      uploadAttemptId,
      urls: ["url" in file ? file.url : undefined],
      fileName: file.name,
    }),
  };

  // Images, documents, and audio files are uploaded directly to S3 (no transcoding)
  if (
    file.type === "image" ||
    file.type === "document" ||
    file.type === "audio"
  ) {
    return withUploadSpan(
      "media_upload.file",
      {
        ...metadata,
        mode: "direct_upload",
      },
      async () => {
        try {
          logUploadBreadcrumb("media_upload.file.start", {
            ...metadata,
            mode: "direct_upload",
          });
          const blob = await blobFromFileToUpload(file, {
            onProgress: (value) => {
              onProgress({
                value: value / 2,
                status: UploadingStatus.uploading,
              });
            },
          });

          const uploadedFileUrl = await uploadFileToS3(
            {
              blob,
              fileName: file.name,
              uploadAttemptId,
              mediaCorrelationKey: metadata.mediaCorrelationKey,
            },
            {
              onProgress(progress) {
                onProgress({
                  value: file.contentType === "url" ? progress / 2 : progress,
                  status: UploadingStatus.uploading,
                });
              },
            }
          );

          onProgress({
            value: 100,
            status: UploadingStatus.uploading,
          });
          logUploadBreadcrumb("media_upload.file.complete", {
            ...metadata,
            mode: "direct_upload",
            uploadedFileUrl,
          });
          return {
            url: uploadedFileUrl,
            uploadAttemptId,
            mediaCorrelationKey: metadata.mediaCorrelationKey,
          };
        } catch (error) {
          logUploadFailure("media_upload.file.failure", error, {
            ...metadata,
            mode: "direct_upload",
            stage: "file_upload",
          });
          throw error;
        }
      }
    );
  } else {
    // video upload
    return file.contentType === "blob" || file.contentType === "getter"
      ? withUploadSpan(
          "media_upload.file",
          {
            ...metadata,
            mode: "video_blob_upload",
          },
          async () => {
            try {
              logUploadBreadcrumb("media_upload.file.start", {
                ...metadata,
                mode: "video_blob_upload",
              });
              const blob = await blobFromFileToUpload(file, {
                onProgress: () => {},
              });
              const uploadedFileUrl = await uploadFileToS3(
                {
                  blob,
                  fileName: file.name,
                  uploadAttemptId,
                  mediaCorrelationKey: metadata.mediaCorrelationKey,
                },
                {
                  onProgress(value) {
                    onProgress({ value, status: UploadingStatus.uploading });
                  },
                }
              );
              const transcodingJobId = await transcodeVideoByUrl(
                uploadedFileUrl,
                {
                  uploadAttemptId,
                  mediaCorrelationKey: metadata.mediaCorrelationKey,
                  fileName: file.name,
                  onProgress: (value) =>
                    onProgress({ value, status: UploadingStatus.transcoding }),
                }
              );
              onProgress({ value: 100, status: UploadingStatus.transcoding });
              logUploadBreadcrumb("media_upload.file.complete", {
                ...metadata,
                mode: "video_blob_upload",
                uploadedFileUrl,
                transcodingJobId,
              });
              return {
                transcodingJobId,
                sourceUrl: uploadedFileUrl,
                uploadAttemptId,
                mediaCorrelationKey: metadata.mediaCorrelationKey,
              };
            } catch (error) {
              logUploadFailure("media_upload.file.failure", error, {
                ...metadata,
                mode: "video_blob_upload",
                stage: "transcode",
              });
              throw error;
            }
          }
        )
      : withUploadSpan(
          "media_upload.file",
          {
            ...metadata,
            mode: "video_remote_transcode",
            sourceUrl: file.url,
          },
          async () => {
            try {
              logUploadBreadcrumb("media_upload.file.start", {
                ...metadata,
                mode: "video_remote_transcode",
                sourceUrl: file.url,
              });
              const transcodingJobId = await transcodeVideoByUrl(file.url, {
                uploadAttemptId,
                mediaCorrelationKey: metadata.mediaCorrelationKey,
                fileName: file.name,
                onProgress: (value) =>
                  onProgress({ value, status: UploadingStatus.transcoding }),
              });
              onProgress({ value: 100, status: UploadingStatus.transcoding });
              logUploadBreadcrumb("media_upload.file.complete", {
                ...metadata,
                mode: "video_remote_transcode",
                sourceUrl: file.url,
                transcodingJobId,
              });
              return {
                transcodingJobId,
                sourceUrl: file.url,
                uploadAttemptId,
                mediaCorrelationKey: metadata.mediaCorrelationKey,
              };
            } catch (error) {
              logUploadFailure("media_upload.file.failure", error, {
                ...metadata,
                mode: "video_remote_transcode",
                sourceUrl: file.url,
                stage: "transcode",
              });
              throw error;
            }
          }
        );
  }
}

async function blobFromFileToUpload(
  file: FileToUpload,
  { onProgress }: { onProgress: (val: number) => void }
): Promise<Blob> {
  switch (file.contentType) {
    case "blob": {
      return file.blob;
    }
    case "getter": {
      return await file.getImage();
    }
    case "url": {
      return await loadUrlAsBlob(file.url, { onProgress });
    }
  }
}
